'use client'

import ResponsivePickerContent from '@trustoo/components/searchPicker/ResponsivePickerContent'
import SearchPicker from '@trustoo/components/searchPicker/SearchPicker'
import {
    SearchPickerOption,
    SearchPickerSettings,
} from '@trustoo/components/searchPicker/SearchPickerContext'
import { SearchPickerInputProps } from '@trustoo/components/searchPicker/SearchPickerInput'
import { TAILWIND_BREAKPOINTS } from '@trustoo/constants/tailwind'
import { ReactNode } from 'react'

export interface BigPickerProps {
    /**
     * Function to fetch options based on query string
     */
    fetchFunction: (query: string) => Promise<SearchPickerOption[]>
    /**
     * Currently selected option
     */
    selectedOption?: SearchPickerOption
    /**
     * Callback when an option is selected
     */
    onSelectOption: (option: SearchPickerOption) => void
    /**
     * Callback when the selection is reset
     */
    onResetOption?: () => void
    /**
     * Props for the input field
     */
    inputFieldProps: SearchPickerInputProps
    /**
     * Options rendered before the fetched options list; part of keyboard navigation (e.g., GeoLocationOption).
     */
    prependedOptions?: ReactNode
    /**
     * Content rendered below the options list (e.g., a no results message)
     */
    listFooterContent?: ReactNode
    /**
     * Settings for positioning, sizing, and debounce
     */
    settings?: Partial<SearchPickerSettings>
    /**
     * Controlled open state
     */
    isOpen?: boolean
    /**
     * Callback when open state changes
     */
    onIsOpenChange?: (isOpen: boolean) => void
    /**
     * Initial query value
     */
    initialQuery?: string
    /**
     * The options shown while nothing is typed — both before the first search and again
     * whenever the input is cleared. Read once, when the picker mounts.
     */
    initialOptions?: SearchPickerOption[]
    /**
     * Window width (in px) below which the picker renders as a full-screen portal instead of a dropdown (default: Tailwind `sm` — 640px)
     */
    portalBreakpoint?: number
    /**
     * Reference element ID for dropdown positioning calculations
     */
    referenceId?: string
    /**
     * Additional className for the root container
     */
    className?: string
}

/**
 * BigPicker - A high-level wrapper around SearchPicker for common use cases
 *
 * This component provides a simplified API for the common pattern of:
 * - SearchPicker.Root
 * - ResponsivePickerContent (custom responsive implementation)
 *
 * It's designed to replace the deprecated BigPicker.js class component
 * with a modern, TypeScript-based implementation using the SearchPicker compound component.
 *
 * **Settings:**
 * The `settings` prop accepts SearchPickerSettings which includes:
 * - `debounceMs`: Debounce delay for search queries (default: 150ms)
 * - `inputFieldHeight`: Height of the input field (default: 46px)
 * - `minHeight`, `maxHeight`: Dropdown height constraints
 * - `marginBottom`: Margin for dropdown positioning
 * - `clearUnselectedQuery`: Empty uncommitted text on close when nothing is selected (default: off)
 *
 * @example
 * Basic usage:
 * ```tsx
 * <BigPicker
 *   fetchFunction={searchForLocation}
 *   selectedOption={selectedOption}
 *   onSelectOption={handleSelectOption}
 *   inputFieldProps={{
 *     before: <Icon name="place" fontSize={21} />,
 *     placeholder: "Search location...",
 *     autoFocus: true,
 *   }}
 * />
 * ```
 *
 * @example
 * With prepended options and list footer (e.g. no-results message):
 * ```tsx
 * <BigPicker
 *   fetchFunction={searchForLocation}
 *   selectedOption={selectedOption}
 *   onSelectOption={handleSelectOption}
 *   inputFieldProps={{
 *     before: <Icon name="place" fontSize={21} />,
 *     placeholder: "Search location...",
 *   }}
 *   prependedOptions={<GeoLocationOption onSelectLocation={handleSelect} />}
 *   listFooterContent={<NoResultsMessage />}
 * />
 * ```
 */
const BigPicker = ({
    fetchFunction,
    selectedOption,
    onSelectOption,
    onResetOption,
    inputFieldProps,
    prependedOptions,
    listFooterContent,
    settings,
    isOpen,
    onIsOpenChange,
    initialQuery,
    initialOptions,
    portalBreakpoint = TAILWIND_BREAKPOINTS.SM,
    referenceId,
    className,
}: BigPickerProps) => {
    return (
        <SearchPicker.Root
            fetchFunction={fetchFunction}
            selectedOption={selectedOption}
            onSelectOption={onSelectOption}
            onResetOption={onResetOption}
            settings={settings}
            isOpen={isOpen}
            onIsOpenChange={onIsOpenChange}
            initialQuery={initialQuery}
            initialOptions={initialOptions}
            className={className}>
            <ResponsivePickerContent
                inputFieldProps={inputFieldProps}
                prependedOptions={prependedOptions}
                listFooterContent={listFooterContent}
                portalBreakpoint={portalBreakpoint}
                referenceId={referenceId}
            />
        </SearchPicker.Root>
    )
}

export default BigPicker
